import { midiToNoteLabel, type PitchExerciseType, type PitchIntervalLevel, type VocalLib } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import { useState } from 'react'
import type { FC } from 'react'
import { TheoryGlyphs } from '../theory-copy/theory-glyphs.js'
import { useVocalCopy } from './use-vocal-copy.js'
import styles from './pitch-training-welcome.module.css'

/**
 * Pitch Trainer welcome screen: the four exercise cards, the interval level picker, the setup
 * advice, and §5.8's calibration gate.
 *
 * The gate is the reason this screen can refuse to start. Without a calibrated range every
 * exercise lands wherever the generator put it, and a bass handed a soprano phrase would have a
 * failure of tessitura recorded as a failure of ear.
 *
 * Spec: docs/superpowers/specs/2026-09-06-vocal-pitch-training-design.md §5.8, §6, §9
 */

const EXERCISE_CARDS: readonly { type: PitchExerciseType; title: string; lexiconId: string }[] = [
  { type: 'tone', title: 'Match a tone', lexiconId: 'vocal-ex-match-tone' },
  { type: 'interval', title: 'Sing an interval', lexiconId: 'vocal-ex-interval' },
  { type: 'vocalise', title: 'Vocalise', lexiconId: 'vocal-ex-vocalise' },
  { type: 'phrase', title: 'Short phrase', lexiconId: 'vocal-ex-phrase' },
]

const LEVELS: readonly PitchIntervalLevel[] = [1, 2, 3]

interface ExerciseCardProps {
  card: (typeof EXERCISE_CARDS)[number]
  selected: boolean
  onSelect: () => void
}

const ExerciseCard: FC<ExerciseCardProps> = ({ card, selected, onSelect }) => {
  const copy = useVocalCopy(card.lexiconId)
  return (
    <button type="button" className={styles.card} aria-pressed={selected} onClick={onSelect}>
      <h3 className={styles.cardTitle}>{card.title}</h3>
      {copy ? (
        <p className={styles.cardBody}>
          <TheoryGlyphs>{copy.body}</TheoryGlyphs>
        </p>
      ) : null}
    </button>
  )
}

export interface PitchTrainingWelcomeProps {
  /** Pre-selected exercise from `?mode=`, or null for no pre-selection. */
  preSelected: PitchExerciseType | null
  /** Pre-selected level from `?level=`. Honoured only for the interval exercise. */
  preSelectedLevel?: PitchIntervalLevel | null
  /** The stored range, or null when the singer has not calibrated. */
  range: VocalLib.VocalRange | null
  onStart: (type: PitchExerciseType, level: PitchIntervalLevel) => void
  onCalibrate: () => void
}

export const PitchTrainingWelcome: FC<PitchTrainingWelcomeProps> = props => {
  const { preSelected, preSelectedLevel, range, onStart, onCalibrate } = props
  const [selected, setSelected] = useState<PitchExerciseType | null>(preSelected)
  const [level, setLevel] = useState<PitchIntervalLevel>(preSelectedLevel ?? 1)

  const intro = useVocalCopy('vocal-intro')
  const privacy = useVocalCopy('vocal-privacy')
  const headphones = useVocalCopy('vocal-headphones')
  const calibrateFirst = useVocalCopy('vocal-calibrate-first')
  const calibrated = useVocalCopy('vocal-range-calibrated', {
    low: range ? midiToNoteLabel(range.low) : undefined,
    high: range ? midiToNoteLabel(range.high) : undefined,
  })

  return (
    <section className={styles.welcome} aria-label="Pitch Trainer welcome">
      <h2 className={styles.title}>Pitch Trainer</h2>
      {intro ? (
        <p className={styles.intro}>
          <TheoryGlyphs>{intro.body}</TheoryGlyphs>
        </p>
      ) : null}

      <div className={styles.cardGrid}>
        {EXERCISE_CARDS.map(card => (
          <ExerciseCard
            key={card.type}
            card={card}
            selected={selected === card.type}
            onSelect={() => setSelected(card.type)}
          />
        ))}
      </div>

      {selected === 'interval' ? (
        <div className={styles.levels} role="group" aria-label="Interval level">
          {LEVELS.map(value => (
            <button
              key={value}
              type="button"
              className={styles.level}
              aria-pressed={level === value}
              onClick={() => setLevel(value)}
            >
              Level {value}
            </button>
          ))}
        </div>
      ) : null}

      <div className={styles.range}>
        {range === null
          ? calibrateFirst && (
              <p className={styles.rangeCopy}>
                <TheoryGlyphs>{calibrateFirst.body}</TheoryGlyphs>
              </p>
            )
          : calibrated && (
              <p className={styles.rangeCopy}>
                <TheoryGlyphs>{calibrated.body}</TheoryGlyphs>
              </p>
            )}
        <Button variant="outlined" size="small" onClick={onCalibrate}>
          {range === null ? 'Calibrate my range' : 'Recalibrate'}
        </Button>
      </div>

      {privacy ? (
        <p className={styles.setup}>
          <TheoryGlyphs>{privacy.body}</TheoryGlyphs>
        </p>
      ) : null}
      {headphones ? (
        <p className={styles.setup}>
          <TheoryGlyphs>{headphones.body}</TheoryGlyphs>
        </p>
      ) : null}

      <Button
        variant="contained"
        color="primary"
        size="large"
        disabled={selected === null || range === null}
        onClick={() => {
          if (selected !== null) {
            onStart(selected, level)
          }
        }}
      >
        Start
      </Button>
    </section>
  )
}
